import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Folder, FileText, Image as ImageIcon, File as FileIcon, Upload, FolderPlus, FolderUp, Search,
  Trash2, Download, Share2, Pencil, RotateCcw, ChevronRight, HardDrive, Users, Archive, X, Copy, Film,
  LayoutGrid, List, Grid2x2, ExternalLink, UploadCloud, History, ChevronUp, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API, downloadBlob } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const SECTIONS = [
  { v: "private", l: "Moje pliki", icon: HardDrive },
  { v: "shared", l: "Wspólny", icon: Users },
  { v: "system", l: "Pliki systemowe", icon: Archive },
];

const CHUNK_THRESHOLD = 20 * 1024 * 1024;
const CHUNK_SIZE = 5 * 1024 * 1024;

const prettySize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fileKind = (ct = "") => {
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("video/")) return "video";
  if (ct === "application/pdf") return "pdf";
  return "other";
};

const OFFICE_EXT = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];
const extOf = (name = "") => (name.split(".").pop() || "").toLowerCase();
const previewKind = (f) => {
  const k = fileKind(f.content_type);
  if (k !== "other") return k;
  const ct = f.content_type || "";
  if (OFFICE_EXT.includes(extOf(f.name)) || ct.includes("officedocument") || ct.includes("ms-excel") || ct.includes("msword") || ct.includes("ms-powerpoint") || ct.includes("opendocument")) return "office";
  return "other";
};

const FileThumb = ({ f }) => {
  const kind = fileKind(f.content_type);
  const thumb = f.thumb_id || (kind === "image" ? f.file_id : null);
  if (thumb) return <img src={`${API}/files/${thumb}/thumb?s=500`} alt="" loading="lazy" className="h-full w-full object-contain bg-[#0D1117]" />;
  const Icon = kind === "pdf" ? FileText : kind === "video" ? Film : FileIcon;
  const color = kind === "pdf" ? "text-rose-400" : kind === "video" ? "text-sky-400" : "text-muted-foreground";
  return <div className="h-full w-full grid place-items-center bg-[#0D1117]"><Icon className={`h-10 w-10 ${color}`} /></div>;
};

const fmtDate = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return ""; } };
const FileGlyph = ({ f }) => { const k = fileKind(f.content_type); const Icon = k === "pdf" ? FileText : k === "video" ? Film : k === "image" ? ImageIcon : FileIcon; return <Icon className="h-4 w-4 text-muted-foreground shrink-0" />; };

const VIEWS = [
  { v: "grid", l: "Kafelki", icon: LayoutGrid },
  { v: "large", l: "Duże miniatury", icon: Grid2x2 },
  { v: "list", l: "Lista szczegółowa", icon: List },
];

const sortItems = (arr, key, dir) => {
  const mul = dir === "asc" ? 1 : -1;
  const val = (it) => key === "size" ? (it.size || 0)
    : key === "updated" ? (it.updated_at || it.created_at || "")
    : (it.name || "").toLowerCase();
  return [...arr].sort((a, b) => {
    const va = val(a), vb = val(b);
    if (va < vb) return -1 * mul;
    if (va > vb) return 1 * mul;
    return 0;
  });
};

export const Drive = () => {
  const confirm = useConfirm();
  const [section, setSection] = useState("private");
  const [trashMode, setTrashMode] = useState(false);
  const [parentId, setParentId] = useState(null);
  const [data, setData] = useState({ breadcrumb: [], folders: [], files: [], readonly: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(null);
  const [folderDlg, setFolderDlg] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [renameItem, setRenameItem] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [shareItem, setShareItem] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [sharePassword, setSharePassword] = useState("");
  const [shareExpiry, setShareExpiry] = useState("");
  const [shareProtected, setShareProtected] = useState(false);
  const [removePassword, setRemovePassword] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [usage, setUsage] = useState(null);
  const [usageOpen, setUsageOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => { try { return localStorage.getItem("drive_view") || "grid"; } catch (_) { return "grid"; } });
  const [sortKey, setSortKey] = useState(() => { try { return localStorage.getItem("drive_sort_key") || "name"; } catch (_) { return "name"; } });
  const [sortDir, setSortDir] = useState(() => { try { return localStorage.getItem("drive_sort_dir") || "asc"; } catch (_) { return "asc"; } });
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [versionsItem, setVersionsItem] = useState(null);
  const [versionsList, setVersionsList] = useState([]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const fileInput = useRef();
  const folderInput = useRef();
  const updateInput = useRef();

  const readonly = section === "system";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (trashMode) {
        const { data: items } = await api.get("/drive/trash", { params: { section } });
        setData({ breadcrumb: [], folders: items.filter((i) => i.type === "folder"), files: items.filter((i) => i.type === "file"), readonly: false });
      } else {
        const { data: res } = await api.get("/drive/list", { params: { section, parent_id: parentId || undefined } });
        setData(res);
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); api.get("/drive/usage").then((r) => setUsage(r.data)).catch(() => {}); }
  }, [section, parentId, trashMode]);

  useEffect(() => { load(); }, [load]);

  const switchSection = (v) => { setSection(v); setParentId(null); setTrashMode(false); setQuery(""); };
  const openFolder = (id) => { setParentId(id); setQuery(""); };
  const goCrumb = (id) => { setParentId(id); };

  // ——— search ———
  const [results, setResults] = useState(null);
  useEffect(() => {
    if (!query.trim() || readonly || trashMode) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data: r } = await api.get("/drive/search", { params: { section, q: query.trim() } });
        setResults(r);
      } catch { setResults({ folders: [], files: [] }); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, section, readonly, trashMode]);

  // ——— upload ———
  const uploadOne = async (file, targetParent) => {
    const parent = targetParent !== undefined ? targetParent : parentId;
    if (file.size <= CHUNK_THRESHOLD) {
      const fd = new FormData();
      fd.append("section", section); if (parent) fd.append("parent_id", parent);
      fd.append("file", file, file.name);
      await api.post("/drive/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return;
    }
    const { data: init } = await api.post("/drive/upload/init", { section, parent_id: parent || null, filename: file.name, content_type: file.type || "application/octet-stream" });
    const total = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", init.upload_id);
      fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
      await api.post("/drive/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProgress({ name: file.name, pct: Math.round(((i + 1) / total) * 100) });
    }
    await api.post("/drive/upload/complete", new URLSearchParams({ upload_id: init.upload_id }), { headers: { "Content-Type": "application/x-www-form-urlencoded" } });
  };

  const handleFiles = async (list) => {
    const files = [...list];
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      setProgress({ name: f.name, pct: 0 });
      try { await uploadOne(f); ok++; }
      catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setProgress(null); setBusy(false);
    if (ok) toast.success(`Wysłano plików: ${ok}`);
    load();
  };

  // ——— folder upload (zachowuje strukturę podkatalogów) ———
  const readEntry = (entry, path = "") => new Promise((resolve) => {
    if (entry.isFile) {
      entry.file((file) => resolve([{ file, rel: path + entry.name }]), () => resolve([]));
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const acc = [];
      const read = () => reader.readEntries((entries) => {
        if (!entries.length) {
          Promise.all(acc.map((e) => readEntry(e, path + entry.name + "/"))).then((r) => resolve(r.flat()));
        } else { acc.push(...entries); read(); }
      }, () => resolve([]));
      read();
    } else resolve([]);
  });

  const uploadWithPaths = async (arr) => {
    if (!arr.length) return;
    setBusy(true);
    const cache = {}; let ok = 0;
    for (const { file, rel } of arr) {
      const dirs = rel.split("/").slice(0, -1); const key = dirs.join("/");
      let folderId = parentId;
      if (dirs.length) {
        if (cache[key] !== undefined) folderId = cache[key];
        else {
          try { const { data } = await api.post("/drive/folder/ensure", { section, parent_id: parentId || null, path: dirs }); folderId = data.folder_id; cache[key] = folderId; }
          catch (e) { toast.error(errMsg(e)); continue; }
        }
      }
      setProgress({ name: rel, pct: 0 });
      try { await uploadOne(file, folderId); ok++; } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
    }
    setProgress(null); setBusy(false);
    if (ok) toast.success(`Wysłano plików: ${ok}`);
    load();
  };

  const handleFolderInput = (list) => uploadWithPaths([...list].map((f) => ({ file: f, rel: f.webkitRelativePath || f.name })));

  const handleDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    if (readonly || trashMode) return;
    const items = e.dataTransfer.items;
    if (items && items.length && items[0].webkitGetAsEntry) {
      const entries = [...items].map((it) => it.webkitGetAsEntry()).filter(Boolean);
      const lists = await Promise.all(entries.map((en) => readEntry(en, "")));
      uploadWithPaths(lists.flat());
    } else {
      handleFiles(e.dataTransfer.files);
    }
  };

  // ——— actions ———
  const createFolder = async () => {
    const name = folderName.trim();
    if (!name) return;
    try {
      await api.post("/drive/folder", { section, parent_id: parentId || null, name });
      setFolderDlg(false); setFolderName(""); toast.success("Folder utworzony"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doRename = async () => {
    const name = renameVal.trim();
    if (!name) return;
    try {
      await api.patch(`/drive/item/${renameItem.id}`, { name });
      setRenameItem(null); toast.success("Zmieniono nazwę"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const trashItem = async (item) => {
    if (!(await confirm({ title: "Przenieść do kosza?", description: `„${item.name}" trafi do kosza. Możesz przywrócić element później.`, confirmText: "Do kosza", danger: true }))) return;
    try { await api.delete(`/drive/item/${item.id}`); toast.success("Przeniesiono do kosza"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const restoreItem = async (item) => {
    try { await api.post(`/drive/item/${item.id}/restore`); toast.success("Przywrócono"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const deleteForever = async (item) => {
    if (!(await confirm({ title: "Usunąć trwale?", description: `„${item.name}" zostanie usunięty bezpowrotnie.`, confirmText: "Usuń trwale", danger: true }))) return;
    try { await api.delete(`/drive/item/${item.id}/permanent`); toast.success("Usunięto trwale"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const emptyTrash = async () => {
    if (!(await confirm({ title: "Opróżnić kosz?", description: "Wszystkie elementy w koszu zostaną usunięte bezpowrotnie.", confirmText: "Opróżnij", danger: true }))) return;
    try { await api.post("/drive/trash/empty", new URLSearchParams({ section }), { headers: { "Content-Type": "application/x-www-form-urlencoded" } }); toast.success("Kosz opróżniony"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const download = (item) => {
    if (item.id?.startsWith("sys-")) { downloadBlob(`/files/${item.file_id}`, item.name); return; }
    downloadBlob(`/drive/item/${item.id}/download`, item.name);
  };

  const openShare = (item) => {
    setShareItem(item);
    setShareUrl(item.share_token ? `${window.location.origin}/dysk/${item.share_token}` : "");
    setSharePassword(""); setRemovePassword(false);
    setShareProtected(!!item.share_protected);
    setShareExpiry(item.share_expires_at ? item.share_expires_at.slice(0, 10) : "");
  };
  const createShareLink = async () => {
    try {
      const { data: r } = await api.post(`/drive/item/${shareItem.id}/share`, { password: sharePassword || null, expires_at: shareExpiry || null, remove_password: removePassword });
      setShareUrl(`${window.location.origin}/dysk/${r.token}`);
      setShareProtected(!!r.protected); setSharePassword(""); setRemovePassword(false);
      toast.success("Link gotowy"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const revokeShare = async () => {
    try { await api.delete(`/drive/item/${shareItem.id}/share`); toast.success("Link wyłączony"); setShareItem(null); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const setView = (v) => { setViewMode(v); try { localStorage.setItem("drive_view", v); } catch (_) {} };

  const toggleSort = (key) => {
    const dir = sortKey === key ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    setSortKey(key); setSortDir(dir);
    try { localStorage.setItem("drive_sort_key", key); localStorage.setItem("drive_sort_dir", dir); } catch (_) {}
  };

  const triggerUpdate = (item) => { setPendingUpdate(item); setTimeout(() => updateInput.current?.click(), 0); };
  const updateOne = async (item, file) => {
    if (file.size <= CHUNK_THRESHOLD) {
      const fd = new FormData(); fd.append("file", file, file.name);
      await api.post(`/drive/item/${item.id}/update`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      return;
    }
    const { data: init } = await api.post("/drive/upload/init", { section, parent_id: parentId || null, filename: file.name, content_type: file.type || "application/octet-stream", replace_item_id: item.id });
    const total = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < total; i++) {
      const fd = new FormData(); fd.append("upload_id", init.upload_id); fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
      await api.post("/drive/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProgress({ name: file.name, pct: Math.round(((i + 1) / total) * 100) });
    }
    await api.post("/drive/upload/complete", new URLSearchParams({ upload_id: init.upload_id }), { headers: { "Content-Type": "application/x-www-form-urlencoded" } });
  };
  const doUpdateFile = async (file) => {
    if (!file || !pendingUpdate) return;
    setBusy(true); setProgress({ name: file.name, pct: 0 });
    try { await updateOne(pendingUpdate, file); toast.success("Plik zaktualizowany"); }
    catch (e) { toast.error(errMsg(e)); }
    setProgress(null); setBusy(false); setPendingUpdate(null); load();
  };
  const openVersions = async (item) => {
    try { const { data } = await api.get(`/drive/item/${item.id}/versions`); setVersionsItem(item); setVersionsList(data.versions || []); setVersionsOpen(true); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const restoreVersion = async (v) => {
    try { await api.post(`/drive/item/${versionsItem.id}/restore-version`, { file_id: v.file_id }); toast.success("Przywrócono wersję"); setVersionsOpen(false); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const openFile = (f) => {
    if (!f.file_id) { toast.error("Brak zawartości pliku do podglądu"); return; }
    const k = previewKind(f);
    if (["image", "pdf", "video", "office"].includes(k)) setPreview(f);
    else download(f);
  };

  const view = results || data;
  const isEmpty = !loading && !view.folders.length && !view.files.length;

  const sectionLabel = useMemo(() => SECTIONS.find((s) => s.v === section)?.l || "", [section]);

  return (
    <div className="space-y-5" data-testid="drive-view"
      onDragOver={(e) => { if (readonly || trashMode) return; e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}>

      {/* header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs text-amber-400 tracking-widest uppercase">Dysk EMPRO</p>
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">{trashMode ? "Kosz" : sectionLabel}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
            {SECTIONS.map((s) => (
              <button key={s.v} data-testid={`drive-section-${s.v}`} onClick={() => switchSection(s.v)}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${section === s.v && !trashMode ? "bg-amber-400 text-[#0D1117]" : "text-muted-foreground hover:text-foreground"}`}>
                <s.icon className="h-3.5 w-3.5" />{s.l}
              </button>
            ))}
          </div>
          {section !== "system" && (
            <button data-testid="drive-trash-toggle" onClick={() => { setTrashMode((t) => !t); setQuery(""); }}
              className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${trashMode ? "border-rose-400 bg-rose-400/10 text-rose-300" : "border-border bg-card text-muted-foreground hover:text-foreground"}`}>
              <Trash2 className="h-3.5 w-3.5" />Kosz
            </button>
          )}
        </div>
      </div>

      {usage && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="drive-usage">
          <span>Twoje pliki: <span className="font-semibold text-foreground">{prettySize(usage.my_bytes)}</span> ({usage.my_count})</span>
          <span>Cała firma: <span className="font-semibold text-foreground">{prettySize(usage.total_bytes)}</span> ({usage.total_count} plików)</span>
          {usage.per_user && <button data-testid="drive-usage-details" onClick={() => setUsageOpen(true)} className="text-amber-400 hover:underline">Zajętość wg pracownika</button>}
        </div>
      )}

      {/* toolbar */}
      {!trashMode && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="drive-search" value={query} onChange={(e) => setQuery(e.target.value)} disabled={readonly}
              placeholder={readonly ? "Pliki systemowe — tylko do odczytu" : "Szukaj plików i folderów w tej sekcji…"} className="pl-9" />
          </div>
          {!readonly && (
            <>
              <Button data-testid="drive-new-folder" variant="outline" className="gap-2" onClick={() => { setFolderDlg(true); setFolderName(""); }}>
                <FolderPlus className="h-4 w-4" />Nowy folder
              </Button>
              <Button data-testid="drive-upload-button" className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-300 font-semibold uppercase tracking-wider" disabled={busy} onClick={() => fileInput.current?.click()}>
                <Upload className="h-4 w-4" />{busy ? "Wysyłanie…" : "Prześlij"}
              </Button>
              <Button data-testid="drive-upload-folder-button" variant="outline" className="gap-2" disabled={busy} onClick={() => folderInput.current?.click()}>
                <FolderUp className="h-4 w-4" />Prześlij folder
              </Button>
              <input ref={fileInput} data-testid="drive-file-input" type="file" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
              <input ref={folderInput} data-testid="drive-folder-input" type="file" multiple webkitdirectory="" directory="" className="hidden" onChange={(e) => { handleFolderInput(e.target.files); e.target.value = ""; }} />
            </>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1" data-testid="drive-view-switch">
          {VIEWS.map((v) => (
            <button key={v.v} data-testid={`drive-view-${v.v}`} onClick={() => setView(v.v)} title={v.l}
              className={`rounded-md p-1.5 transition-colors ${viewMode === v.v ? "bg-amber-400 text-[#0D1117]" : "text-muted-foreground hover:text-foreground"}`}>
              <v.icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </div>

      {trashMode && (view.folders.length || view.files.length) ? (
        <Button data-testid="drive-empty-trash" variant="outline" className="gap-2 border-rose-400/50 text-rose-300 hover:bg-rose-400/10" onClick={emptyTrash}>
          <Trash2 className="h-4 w-4" />Opróżnij kosz
        </Button>
      ) : null}

      {/* breadcrumb */}
      {!trashMode && !results && (
        <div className="flex flex-wrap items-center gap-1 text-sm" data-testid="drive-breadcrumb">
          <button onClick={() => setParentId(null)} className={`font-semibold ${parentId ? "text-amber-400 hover:underline" : "text-foreground"}`}>{sectionLabel}</button>
          {view.breadcrumb?.map((c) => (
            <span key={c.id} className="flex items-center gap-1">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              <button onClick={() => goCrumb(c.id)} className="text-amber-400 hover:underline">{c.name}</button>
            </span>
          ))}
        </div>
      )}

      {/* progress */}
      {progress && (
        <div className="rounded-lg border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-sm" data-testid="drive-upload-progress">
          Wysyłanie <span className="font-semibold">{progress.name}</span> — {progress.pct}%
        </div>
      )}

      {/* dropzone hint */}
      {dragOver && !readonly && !trashMode && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-[#0D1117]/70 backdrop-blur-sm pointer-events-none">
          <div className="rounded-2xl border-2 border-dashed border-amber-400 bg-card px-10 py-8 text-center">
            <Upload className="h-10 w-10 mx-auto mb-3 text-amber-400" />
            <p className="font-semibold">Upuść pliki lub foldery, aby przesłać do „{sectionLabel}"</p>
          </div>
        </div>
      )}

      {/* grid */}
      {loading ? (
        <p className="text-sm text-muted-foreground py-10 text-center" data-testid="drive-loading">Ładowanie…</p>
      ) : isEmpty ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center" data-testid="drive-empty">
          <HardDrive className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{trashMode ? "Kosz jest pusty." : readonly ? "Brak plików w tej sekcji." : "Pusto tutaj — przeciągnij pliki lub kliknij „Prześlij”."}</p>
        </div>
      ) : viewMode === "list" ? (
        <div className="rounded-xl border border-border overflow-x-auto" data-testid="drive-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                <SortTh label="Nazwa" col="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-left px-3 py-2" />
                <SortTh label="Rozmiar" col="size" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-left px-3 py-2 w-24 hidden sm:table-cell" />
                <SortTh label="Zmodyfikowano" col="updated" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-left px-3 py-2 w-32 hidden md:table-cell" />
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {sortItems(view.folders, sortKey, sortDir).map((fo) => (
                <tr key={fo.id} data-testid={`drive-folder-${fo.id}`} className="border-b border-border/40 hover:bg-card">
                  <td className="px-3 py-2"><button onClick={() => !trashMode && openFolder(fo.id)} disabled={trashMode} className="flex items-center gap-2 text-left max-w-full"><Folder className="h-4 w-4 text-amber-400 shrink-0" /><span className="font-medium truncate">{fo.name}</span></button></td>
                  <td className="px-3 py-2 text-muted-foreground hidden sm:table-cell">—</td>
                  <td className="px-3 py-2 text-muted-foreground hidden md:table-cell">{fmtDate(fo.updated_at || fo.created_at)}</td>
                  <td className="px-2 py-2"><div className="flex justify-end"><ItemMenu item={fo} inline trashMode={trashMode} readonly={readonly} onShare={() => openShare(fo)} onRename={() => { setRenameItem(fo); setRenameVal(fo.name); }} onTrash={() => trashItem(fo)} onRestore={() => restoreItem(fo)} onDeleteForever={() => deleteForever(fo)} /></div></td>
                </tr>
              ))}
              {sortItems(view.files, sortKey, sortDir).map((f) => (
                <tr key={f.id} data-testid={`drive-file-${f.id}`} className="border-b border-border/40 hover:bg-card">
                  <td className="px-3 py-2"><button onClick={() => !trashMode && openFile(f)} disabled={trashMode} className="flex items-center gap-2 text-left w-full"><FileGlyph f={f} /><span className="font-medium truncate">{f.name}</span></button></td>
                  <td className="px-3 py-2 text-muted-foreground hidden sm:table-cell">{prettySize(f.size)}</td>
                  <td className="px-3 py-2 text-muted-foreground hidden md:table-cell">{fmtDate(f.updated_at || f.created_at)}</td>
                  <td className="px-2 py-2"><div className="flex justify-end"><ItemMenu item={f} isFile inline trashMode={trashMode} readonly={readonly} onOpen={() => download(f)} onDownload={() => download(f)} onUpdate={() => triggerUpdate(f)} onVersions={() => openVersions(f)} onShare={() => openShare(f)} onRename={() => { setRenameItem(f); setRenameVal(f.name); }} onTrash={() => trashItem(f)} onRestore={() => restoreItem(f)} onDeleteForever={() => deleteForever(f)} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={viewMode === "large" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3"} data-testid="drive-grid">
          {view.folders.map((fo) => (
            <div key={fo.id} data-testid={`drive-folder-${fo.id}`}
              className="group relative rounded-xl border border-border bg-card p-3 hover:border-amber-400/60 transition-colors">
              <button onClick={() => !trashMode && openFolder(fo.id)} className="w-full text-left" disabled={trashMode}>
                <div className={`flex items-center justify-center ${viewMode === "large" ? "h-28" : "h-20"}`}><Folder className={viewMode === "large" ? "h-16 w-16 text-amber-400" : "h-12 w-12 text-amber-400"} /></div>
                <p className="mt-2 text-sm font-semibold truncate">{fo.name}</p>
                <p className="text-[11px] text-muted-foreground">Folder</p>
              </button>
              <ItemMenu item={fo} trashMode={trashMode} readonly={readonly}
                onShare={() => openShare(fo)} onRename={() => { setRenameItem(fo); setRenameVal(fo.name); }} onTrash={() => trashItem(fo)}
                onRestore={() => restoreItem(fo)} onDeleteForever={() => deleteForever(fo)} />
            </div>
          ))}
          {view.files.map((f) => (
            <div key={f.id} data-testid={`drive-file-${f.id}`}
              className="group relative rounded-xl border border-border bg-card overflow-hidden hover:border-amber-400/60 transition-colors">
              <button onClick={() => !trashMode && openFile(f)} className="w-full text-left" disabled={trashMode}>
                <div className={viewMode === "large" ? "h-44" : "h-24"}><FileThumb f={f} /></div>
                <div className="p-3">
                  <p className="text-sm font-semibold truncate">{f.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{prettySize(f.size)}{f.client_name ? ` · ${f.client_name}` : ""}</p>
                </div>
              </button>
              <ItemMenu item={f} isFile trashMode={trashMode} readonly={readonly}
                onOpen={() => download(f)} onDownload={() => download(f)} onUpdate={() => triggerUpdate(f)} onVersions={() => openVersions(f)}
                onShare={() => openShare(f)} onRename={() => { setRenameItem(f); setRenameVal(f.name); }}
                onTrash={() => trashItem(f)} onRestore={() => restoreItem(f)} onDeleteForever={() => deleteForever(f)} />
            </div>
          ))}
        </div>
      )}

      {/* preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl" data-testid="drive-preview-dialog">
          <DialogHeader><DialogTitle className="truncate pr-8">{preview?.name}</DialogTitle></DialogHeader>
          {preview && (() => {
            const k = previewKind(preview);
            const fileUrl = `${API}/files/${preview.file_id}`;
            if (k === "pdf") return <iframe title="podgląd" src={fileUrl} className="w-full h-[70vh] rounded-lg bg-white" />;
            if (k === "image") return <img src={fileUrl} alt="" className="w-full max-h-[70vh] object-contain rounded-lg bg-[#0D1117]" />;
            if (k === "video") return <video data-testid="drive-preview-video" src={`${API}/drive/raw/${preview.file_id}`} controls className="w-full max-h-[70vh] rounded-lg bg-black" />;
            if (k === "office") return <iframe title="podgląd" data-testid="drive-preview-office" src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`} className="w-full h-[70vh] rounded-lg bg-white" />;
            return <p className="text-sm text-muted-foreground py-8 text-center">Podgląd niedostępny — pobierz plik, aby go otworzyć.</p>;
          })()}
          <DialogFooter className="gap-2">
            <Button data-testid="drive-preview-open" variant="outline" className="gap-2" onClick={() => download(preview)}><ExternalLink className="h-4 w-4" />Otwórz w programie</Button>
            {!readonly && <Button data-testid="drive-preview-update" variant="outline" className="gap-2" onClick={() => { const p = preview; setPreview(null); triggerUpdate(p); }}><UploadCloud className="h-4 w-4" />Zaktualizuj plik</Button>}
            <Button data-testid="drive-preview-download" className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-300 font-semibold" onClick={() => download(preview)}><Download className="h-4 w-4" />Pobierz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* new folder */}
      <Dialog open={folderDlg} onOpenChange={setFolderDlg}>
        <DialogContent data-testid="drive-folder-dialog">
          <DialogHeader><DialogTitle>Nowy folder</DialogTitle></DialogHeader>
          <Input data-testid="drive-folder-name" autoFocus value={folderName} onChange={(e) => setFolderName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createFolder()} placeholder="Nazwa folderu" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFolderDlg(false)}>Anuluj</Button>
            <Button data-testid="drive-folder-create" className="bg-amber-400 text-[#0D1117] hover:bg-amber-300 font-semibold" onClick={createFolder}>Utwórz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* rename */}
      <Dialog open={!!renameItem} onOpenChange={(o) => !o && setRenameItem(null)}>
        <DialogContent data-testid="drive-rename-dialog">
          <DialogHeader><DialogTitle>Zmień nazwę</DialogTitle></DialogHeader>
          <Input data-testid="drive-rename-input" autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doRename()} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameItem(null)}>Anuluj</Button>
            <Button data-testid="drive-rename-save" className="bg-amber-400 text-[#0D1117] hover:bg-amber-300 font-semibold" onClick={doRename}>Zapisz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* share */}
      <Dialog open={!!shareItem} onOpenChange={(o) => !o && setShareItem(null)}>
        <DialogContent data-testid="drive-share-dialog">
          <DialogHeader><DialogTitle>Udostępnij link</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Utwórz link, którym udostępnisz {shareItem?.type === "folder" ? "folder" : "plik"} „{shareItem?.name}” osobom spoza firmy.</p>
          <div className="space-y-3">
            {shareProtected && (
              <div className="flex items-center justify-between rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs" data-testid="drive-share-protected-badge">
                <span className="font-semibold text-amber-300">Link chroniony hasłem</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input data-testid="drive-share-remove-password" type="checkbox" checked={removePassword} onChange={(e) => setRemovePassword(e.target.checked)} />
                  Usuń hasło
                </label>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-muted-foreground">{shareProtected ? "Nowe hasło (opcjonalnie)" : "Hasło (opcjonalnie)"}</label>
              <Input data-testid="drive-share-password" type="text" value={sharePassword} disabled={removePassword}
                onChange={(e) => setSharePassword(e.target.value)}
                placeholder={shareProtected ? "Pozostaw puste, aby nie zmieniać hasła" : "Bez hasła — otworzy każdy z linkiem"} />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Wygasa dnia (opcjonalnie)</label>
              <Input data-testid="drive-share-expiry" type="date" value={shareExpiry} onChange={(e) => setShareExpiry(e.target.value)} />
            </div>
            <Button data-testid="drive-share-generate" className="w-full bg-amber-400 text-[#0D1117] hover:bg-amber-300 font-semibold" onClick={createShareLink}>{shareUrl ? "Zaktualizuj link" : "Utwórz link"}</Button>
          </div>
          {shareUrl && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input data-testid="drive-share-url" readOnly value={shareUrl} onFocus={(e) => e.target.select()} />
                <Button variant="outline" className="gap-2 shrink-0" onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success("Skopiowano link"); }}><Copy className="h-4 w-4" />Kopiuj</Button>
              </div>
              {sharePassword && <p className="text-[11px] text-amber-300">Pamiętaj, aby hasło przekazać odbiorcy osobno.</p>}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="drive-share-revoke" variant="ghost" className="text-rose-300 hover:text-rose-200" onClick={revokeShare}>Wyłącz link</Button>
            <Button className="bg-secondary text-foreground hover:bg-secondary/80" onClick={() => setShareItem(null)}>Zamknij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ukryty input do nadpisywania pliku */}
      <input ref={updateInput} data-testid="drive-update-input" type="file" className="hidden" onChange={(e) => { doUpdateFile(e.target.files?.[0]); e.target.value = ""; }} />

      {/* historia wersji */}
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent data-testid="drive-versions-dialog">
          <DialogHeader><DialogTitle className="truncate pr-8">Historia wersji — {versionsItem?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {versionsList.length ? versionsList.map((v, i) => (
              <div key={v.file_id} className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="font-medium truncate">Wersja {versionsList.length - i} · {prettySize(v.size)}</p>
                  <p className="text-[11px] text-muted-foreground">{fmtDate(v.replaced_at)}{v.replaced_by ? ` · ${v.replaced_by}` : ""}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`drive-version-download-${v.file_id}`} title="Pobierz" onClick={() => downloadBlob(`/files/${v.file_id}`, versionsItem.name)} className="rounded-md p-1.5 text-muted-foreground hover:text-amber-400"><Download className="h-4 w-4" /></button>
                  <button data-testid={`drive-version-restore-${v.file_id}`} title="Przywróć tę wersję" onClick={() => restoreVersion(v)} className="rounded-md p-1.5 text-muted-foreground hover:text-emerald-400"><RotateCcw className="h-4 w-4" /></button>
                </div>
              </div>
            )) : <p className="text-sm text-muted-foreground">Brak wcześniejszych wersji — pojawią się po pierwszym nadpisaniu pliku.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* usage per-user (admin) */}
      <Dialog open={usageOpen} onOpenChange={setUsageOpen}>
        <DialogContent data-testid="drive-usage-dialog">
          <DialogHeader><DialogTitle>Zajętość miejsca wg pracownika</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {usage?.per_user?.length ? usage.per_user.map((u) => (
              <div key={u.id} className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm">
                <span className="font-semibold truncate">{u.name}</span>
                <span className="font-mono text-muted-foreground shrink-0">{prettySize(u.bytes)} · {u.count} plików</span>
              </div>
            )) : <p className="text-sm text-muted-foreground">Brak plików.</p>}
          </div>
          <div className="flex items-center justify-between border-t border-border pt-3 text-sm font-semibold">
            <span>Cała firma</span>
            <span className="font-mono">{prettySize(usage?.total_bytes)} · {usage?.total_count} plików</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SortTh = ({ label, col, sortKey, sortDir, onSort, className }) => {
  const active = sortKey === col;
  return (
    <th className={`font-semibold ${className}`}>
      <button data-testid={`drive-sort-${col}`} onClick={() => onSort(col)}
        className={`flex items-center gap-1 uppercase tracking-wider transition-colors ${active ? "text-amber-400" : "hover:text-foreground"}`}>
        {label}
        {active && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </button>
    </th>
  );
};

const ItemMenu = ({ item, isFile, trashMode, readonly, inline, onOpen, onDownload, onUpdate, onVersions, onShare, onRename, onTrash, onRestore, onDeleteForever }) => {
  if (trashMode) {
    return (
      <div className={inline ? "flex gap-1" : "absolute top-2 right-2 flex gap-1"}>
        <button data-testid={`drive-restore-${item.id}`} onClick={onRestore} title="Przywróć" className="rounded-md bg-background/80 p-1.5 text-muted-foreground hover:text-emerald-400"><RotateCcw className="h-4 w-4" /></button>
        <button data-testid={`drive-delete-forever-${item.id}`} onClick={onDeleteForever} title="Usuń trwale" className="rounded-md bg-background/80 p-1.5 text-muted-foreground hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
      </div>
    );
  }
  if (readonly) {
    return isFile && item.file_id ? (
      <button data-testid={`drive-download-${item.id}`} onClick={onDownload} title="Pobierz" className={inline ? "rounded-md bg-background/80 p-1.5 text-muted-foreground hover:text-amber-400" : "absolute top-2 right-2 rounded-md bg-background/80 p-1.5 text-muted-foreground hover:text-amber-400"}><Download className="h-4 w-4" /></button>
    ) : null;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`drive-menu-${item.id}`} className={inline ? "rounded-md bg-background/80 p-1.5 text-muted-foreground hover:text-foreground" : "absolute top-2 right-2 rounded-md bg-background/80 p-1.5 text-muted-foreground opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:text-foreground"}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {isFile && onOpen && <DropdownMenuItem data-testid={`drive-action-open-${item.id}`} onClick={onOpen}><ExternalLink className="h-4 w-4 mr-2" />Otwórz w programie</DropdownMenuItem>}
        {isFile && onUpdate && <DropdownMenuItem data-testid={`drive-action-update-${item.id}`} onClick={onUpdate}><UploadCloud className="h-4 w-4 mr-2" />Zaktualizuj plik</DropdownMenuItem>}
        {isFile && onVersions && <DropdownMenuItem data-testid={`drive-action-versions-${item.id}`} onClick={onVersions}><History className="h-4 w-4 mr-2" />Historia wersji</DropdownMenuItem>}
        {isFile && <DropdownMenuItem data-testid={`drive-action-download-${item.id}`} onClick={onDownload}><Download className="h-4 w-4 mr-2" />Pobierz</DropdownMenuItem>}
        {onShare && <DropdownMenuItem data-testid={`drive-action-share-${item.id}`} onClick={onShare}><Share2 className="h-4 w-4 mr-2" />Udostępnij link</DropdownMenuItem>}
        <DropdownMenuItem data-testid={`drive-action-rename-${item.id}`} onClick={onRename}><Pencil className="h-4 w-4 mr-2" />Zmień nazwę</DropdownMenuItem>
        <DropdownMenuItem data-testid={`drive-action-trash-${item.id}`} onClick={onTrash} className="text-rose-400 focus:text-rose-400"><Trash2 className="h-4 w-4 mr-2" />Do kosza</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
